import { ReactElement, useMemo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Form, Heading } from '@appsumo/dorado-react';
import { yupResolver } from '@hookform/resolvers/yup';

import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';
import { useEvent } from '~/lib/events';
import {
  ElementOptionAddressProps,
  FormRefProps,
  PaymentMethodProps,
  SelectedPaymentType,
} from '~/types/checkout';
import { ADDITIONAL_PAYMENT_METHODS, STEP } from '~/constants/checkout';
import { canPayWithCredits, canPayWithPayPal } from '~/lib/checkout/util';

import PaymentOption from './PaymentOption';
import PaymentOptionCredits from './PaymentOptionCredits';
import { CheckoutCard } from '../Global';

const { NEW_PAYMENT_METHOD, PAYPAL_PAYMENT_METHOD } =
  ADDITIONAL_PAYMENT_METHODS;

function PaymentDetails({
  isCreditsCouponsEnabled,
}: Readonly<{
  isCreditsCouponsEnabled: boolean;
}>): ReactElement {
  const ref = useRef<FormRefProps>();
  const { billingDetails, cart, paymentMethods, defaultPaymentMethod, step } =
    useCheckoutState();
  const { setSelectedPaymentMethod, setStepErrorMessage } = useCheckoutAPI();
  const [isPaymentPending, setIsPaymentPending] = useState(false);

  useEvent('payment:pending', () => setIsPaymentPending(true));
  useEvent('payment:reset', () => setIsPaymentPending(false));

  const showPayPal: boolean = canPayWithPayPal(cart);

  const disablePM = canPayWithCredits(cart);
  const paymentOptionClass = clsx({
    'pointer-events-none select-none opacity-50': disablePM || isPaymentPending,
  });

  // Create an address object to pass to the payment element
  // Optional postal code
  const address = {
    country: `${billingDetails.country.value}`,
    ...(billingDetails.postalCode && {
      postal_code: billingDetails.postalCode,
    }),
  } as ElementOptionAddressProps;

  const existingCardPaymentOptions = useMemo(
    () =>
      paymentMethods.map((paymentMethod: PaymentMethodProps) => (
        <PaymentOption
          key={paymentMethod.id}
          type="existing-card"
          cardDetails={paymentMethod}
          defaultPaymentMethod={defaultPaymentMethod}
          canPayWithCredits={disablePM}
        />
      )),
    [paymentMethods, defaultPaymentMethod, disablePM],
  );

  function onSelectPaymentMethod(data: FieldValues) {
    if (isPaymentPending) return;
    setSelectedPaymentMethod(data.paymentMethod as SelectedPaymentType);
    setStepErrorMessage(STEP.PAYMENT, '');
  }

  if (step !== STEP.PAYMENT) return <></>;

  return (
    <>
      <Heading.H3>Payment method</Heading.H3>
      <div className="w-full">
        {/*
          todo: let's have a form type where onSubmit+resolver can be optional?

          in this specific case, we don't need to necessarily 'submit' the form,
          but having the form parent component allows us to:
            1. group radio's together
            2. watch/listen to radio changes easily
        */}
        <Form
          onSubmit={() => {}}
          resolver={yupResolver(Yup.object({}))}
          ref={ref}
          autoClearErrors
          watch={onSelectPaymentMethod}
        >
          {!isCreditsCouponsEnabled && (
            <PaymentOptionCredits
              disablePM={disablePM}
              isPaymentPending={isPaymentPending}
            />
          )}
          <div
            className={paymentOptionClass}
            data-testid="payment-checkout-payment-methods"
          >
            {existingCardPaymentOptions}
            <PaymentOption
              type={NEW_PAYMENT_METHOD}
              address={address}
              defaultPaymentMethod={defaultPaymentMethod}
              canPayWithCredits={disablePM}
              isLastPaymentOption={!showPayPal}
            />
            <div className={clsx({ hidden: !showPayPal })}>
              <PaymentOption
                type={PAYPAL_PAYMENT_METHOD}
                defaultPaymentMethod={defaultPaymentMethod}
                canPayWithCredits={disablePM}
              />
            </div>
          </div>
        </Form>
      </div>
    </>
  );
}

function PaymentDetailsMinimized(): ReactElement {
  const { step } = useCheckoutState();
  if (step === STEP.PAYMENT) return <></>;

  return (
    <Heading.H3 className="text-slate" data-testid="payment-method-title">
      Payment method
    </Heading.H3>
  );
}

export default function Payment({
  isCreditsCouponsEnabled,
}: Readonly<{
  isCreditsCouponsEnabled: boolean;
}>): ReactElement {
  // This is moved outside other components in case they switch back to billing and keep the previous
  // payment method selected. The defaultValue doesn't update after a value is set
  // (fallback is new payment method), so use the paymentMethods if any are available

  return (
    <CheckoutCard step={STEP.PAYMENT}>
      <PaymentDetails isCreditsCouponsEnabled={isCreditsCouponsEnabled} />
      <PaymentDetailsMinimized />
    </CheckoutCard>
  );
}
